import { type FC, useEffect } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ModalLayout from 'components/modals/baseModal';

import { sendAnalyticEvent } from 'data/actions/analytics';
import { toggleModal } from 'data/actions/modals';
import { modalOptionsSelector } from 'data/selectors/modals';

import { PAGE_LINKS } from 'ts/constants/page-links';

import { useFileDownload } from '../../useFileDownload';
import { useOneTimeUpsaleProducts } from '../helpers/useOneTimeUpsaleProducts';
import { ReactComponent as CloseIcon } from './assets/closeIcon.svg';
import coverIcon from './assets/conver.svg';
import downloadIcon from './assets/donwload.svg';
import editIcon from './assets/edit.svg';
import unlimitedIcon from './assets/unlimited.svg';

const bullets = [
  {
    label: <Trans i18nKey='upsale_modal.success_modal.bullets.1' />,
    icon: editIcon,
    key: 'edit_text_and_images'
  },
  {
    label: <Trans i18nKey='upsale_modal.success_modal.bullets.2' />,
    icon: unlimitedIcon,
    key: 'unlimited_credits'
  },
  {
    label: <Trans i18nKey='upsale_modal.success_modal.bullets.3' />,
    icon: downloadIcon,
    key: 'download_files'
  }
];

export const UpsaleSuccessModal: FC = () => {
  const dispatch = useDispatch();
  const { skip } = useSelector(modalOptionsSelector);
  const { currentPlace } = useOneTimeUpsaleProducts();
  const { downloadResult } = useFileDownload();
  const { t } = useTranslation();

  const handleCloseModal = () => {
    dispatch(
      sendAnalyticEvent({
        event: 'extention_promo_close_tap',
        data: {
          placement: currentPlace()
        }
      })
    );
    dispatch(toggleModal({ visible: false }));
    if (window.location.pathname.includes('payment-success')) {
      downloadResult();
    }
  };

  useEffect(() => {
    dispatch(
      sendAnalyticEvent({
        event: 'extention_promo_modal_view',
        data: {
          placement: currentPlace()
        }
      })
    );
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ModalLayout>
      <div className='bg-white rounded-xl tablet:min-w-[646px] tablet:max-w-[646px]'>
        <div className='relative'>
          <button
            onClick={handleCloseModal}
            className='absolute top-2 right-2 tablet:top-4 tablet:right-4'
          >
            <CloseIcon />
          </button>
          <img
            src={coverIcon}
            alt='cover'
          />
        </div>
        <div className='p-4 tablet:p-10 flex flex-col gap-3 tablet:gap-6'>
          <h2 className='text-[20px] font-[700] text-center tablet:text-left tablet:text-[32px] tablet:font-extrabold tablet:leading-9'>
            {skip ? t('upsale_modal.skip_modal.title') : t('upsale_modal.success_modal.title')}
          </h2>
          <ul className='flex flex-col gap-4'>
            {bullets.map((bullet) => (
              <li
                key={bullet.key}
                className='flex items-center gap-2'
              >
                <img
                  src={bullet.icon}
                  alt={bullet.key}
                />
                <p className='text-[#1D1D1D] text-[16px] tablet:text-[20px] font-[500] tablet:leading-6'>
                  {bullet.label}
                </p>
              </li>
            ))}
          </ul>
          <a
            href={PAGE_LINKS.EXTENSION_LINK}
            target='_blank'
            rel='noreferrer'
            className='bg-[#5F30E2] rounded-2xl py-4 px-6 text-center text-white text-[18px] font-[700] leading-6'
            onClick={() => {
              dispatch(
                sendAnalyticEvent({
                  event: 'extention_promo_accept_tap',
                  data: {
                    placement: currentPlace()
                  }
                })
              );
            }}
          >
            {t('upsale_modal.success_modal.install_extension')}
          </a>
        </div>
      </div>
    </ModalLayout>
  );
};
